import "./Profile.scss";
import { useState } from "react";
import {
  FormControl,
  IconButton,
  InputAdornment,
  OutlinedInput,
  TextField,
  Modal,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import GreenButton from "../../components/GreenButton/GreenButton";
import { useAuth } from "../../context/AuthContext";
import OtpInput from "react-otp-input";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import api from "../../utils/api";
import Logo from "./../../assets/images/MainLogo.svg";
import { useLanguage } from "../../context/LanguageContext";
import MessageModals from "../../components/MessageModals/MessageModals";
import { validatePhone } from "../../utils/validatePhone";

const Profile = () => {
  const { user, updateUserData, updatePassword } = useAuth();
  const { t } = useLanguage();
  const [toggle, setToggle] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [otp, setOtp] = useState("");
  const [formData, setFormData] = useState({
    name: user?.name || "",
    phoneNumber: user?.phoneNumber || "",
    oldPassword: "",
    password: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});
  const [phoneHasChanged, setPhoneHasChanged] = useState(false);

  // Password visibility toggle handlers
  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleClickShowOldPassword = () => setShowOldPassword((show) => !show);
  const handleClickShowConfirmPassword = () =>
    setShowConfirmPassword((show) => !show);

  // Prevent form submission when clicking visibility toggle
  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Check if phone number has changed
    if (name === "phoneNumber" && value !== user.phoneNumber) {
      setPhoneHasChanged(true);
    } else if (name === "phoneNumber" && value === user.phoneNumber) {
      setPhoneHasChanged(false);
    }

    // Clear error when field is edited
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };



  const validateProfileForm = () => {
    const newErrors = {};

    // Validate name
    if (!formData.name.trim()) {
      newErrors.name = t("nameRequired");
    }

    // Validate phone number if it has changed
    if (phoneHasChanged) {
      if (!formData.phoneNumber) {
        newErrors.phoneNumber = t("phoneRequired");
      } else if (!validatePhone(formData.phoneNumber)) {
        newErrors.phoneNumber = t("invalidPhone");
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validatePasswordForm = () => {
    const newErrors = {};

    if (!formData.oldPassword) {
      newErrors.oldPassword = t("currentPasswordRequired");
    }

    if (!formData.password) {
      newErrors.password = t("newPasswordRequired");
    } else if (formData.password.length < 6) {
      newErrors.password = t("passwordMinLength");
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = t("confirmPasswordRequired");
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = t("passwordsMismatch");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    if (!validateProfileForm()) return;

    setIsLoading(true);
    try {
      // If phone number has changed, request OTP verification
      if (phoneHasChanged) {
        // Request OTP
        // const response = await api.post("/auth/users/requestOtp", {
        const response = await api.patch("/users/phoneNumber/request-otp", {
          phoneNumber: formData.phoneNumber,
        });

        if (response.data.status === "success") {
          toast.info(t("otpSent"));
          setShowOtpModal(true);
        }
      } else {
        // Only update name
        const response = await api.patch("/users/updateMe", {
          name: formData.name,
        });

        if (response.data.status === "success") {
          // Update user data in context and localStorage
          updateUserData(response.data.data);
          toast.success(t("updateSuccess"));
        }
      }
    } catch (error) {
      console.error("Update error:", error);
      const errorMessage = error.response?.data?.message || t("updateError");
      setErrors({ general: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    if (!validatePasswordForm()) return;

    setIsLoading(true);
    try {
      // Use the updatePassword function from AuthContext
      const success = await updatePassword({
        oldPassword: formData.oldPassword,
        password: formData.password,
        confirmPassword: formData.confirmPassword,
      });

      if (success) {
        // Password updated successfully and user will be logged out
        // No need to clear password fields as the component will unmount
      }
    } catch (error) {
      console.error("Password update error:", error);
      const errorMessage =
        error.response?.data?.message || t("passwordUpdateError");
      setErrors({ general: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (!otp || otp.length !== 4) {
      setErrors({ otp: t("enterOtpCode") });
      return;
    }

    setIsLoading(true);
    try {
      // First verify OTP
      const verifyResponse = await api.patch("/users/phoneNumber/verify-otp", {
        otp,
        phoneNumber: formData.phoneNumber,
      });

   updateUserData(verifyResponse.data.user);
   setShowOtpModal(false);
   toast.success(t("updateSuccess"));
   setPhoneHasChanged(false);
 
    } catch (error) {
      // Handle rate limit error (429)
      if (error.response?.status === 429) {
        setErrors({
          otp: t("rateLimitExceeded"),
        });
      } else {
        const message = error.response?.data?.message || t("verificationError");
        setErrors({ otp: message });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setIsLoading(true);
    try {
      const response = await api.post("/auth/users/requestOtp", {
        phoneNumber: formData.phoneNumber,
      });

      if (response.data.status === "success") {
        toast.info(t("otpResent"));
      }
    } catch (error) {
      // Handle rate limit error (429)
      if (error.response?.status === 429) {
        setErrors({
          otp: t("rateLimitExceeded"),
        });
      } else {
        const message = error.response?.data?.message || t("resendError");
        setErrors({ otp: message });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const renderErrorMessage = (field) => {
    return errors[field] ? (
      <div
        className="error-message"
        style={{ color: "#e51937", fontSize: "12px", marginTop: "5px" }}
      >
        {errors[field]}
      </div>
    ) : null;
  };

  const renderPasswordField = (label, name, showState, toggleShowFn) => (
    <FormControl sx={{ m: 1, flex: 1, width: "440px" }} variant="outlined">
      <label className="form-label">{label}</label>
      <OutlinedInput
        type={showState ? "text" : "password"}
        name={name}
        value={formData[name] || ""}
        onChange={handleChange}
        error={!!errors[name]}
        endAdornment={
          <InputAdornment position="start">
            <IconButton
              onClick={toggleShowFn}
              onMouseDown={handleMouseDownPassword}
              edge="end"
            >
              {showState ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </InputAdornment>
        }
      />
      {renderErrorMessage(name)}
    </FormControl>
  );




  const renderOtpModal = () => (
  <Modal
    open={showOtpModal}
    onClose={() => setShowOtpModal(false)}
    aria-labelledby="otp-modal-title"
    aria-describedby="otp-modal-description"
  >

    
    <div className="active-code-profile" dir="rtl">
      <img src={Logo} alt="Logo" />
      <p>{t("verificationCode")}</p>
      <span>{t("enterVerificationCode")}</span>
      {errors.otp && (
        <div
          className="error-message"
          style={{ color: "#e51937", marginBottom: "10px" }}
        >
          {errors.otp}
        </div>
      )}
      <form onSubmit={handleVerifyOTP} >
        <div dir="ltr" style={{ width: "100%" ,display:"flex", justifyContent: "center"}}>
          <OtpInput
            value={otp}
            onChange={setOtp}
            numInputs={4}
            renderInput={(props) => <input {...props} />}
            containerStyle={{ gap: "10px", direction: "ltr" }}
            justifyContent="center"
            inputStyle={{
              width: "50px",
              height: "50px",
              margin: "0 5px",
              fontSize: "24px",
              borderRadius: "5px",
              border: "1px solid #ddd",
            }}
          />
        </div>
        <div className="timer" style={{ margin: "15px 0" }}>
          <a
            className="re-send"
            style={{ color: "#5cb85c", cursor: "pointer" }}
            onClick={handleResendOTP}
          >
            {isLoading ? t("loading") : t("resendCode")}
          </a>
        </div>

        <GreenButton
          text={isLoading ? t("loading") : t("confirm")}
          size="full"
          variant="fill"
          type="submit"
          disabled={isLoading}
        />
      </form>
    </div>
  </Modal>
);

  return (
    <div className="profile">
      <div className="container">
        <h2 className="section-title">{t("profile")}</h2>
        {/* <p className="section-description">{t("profileDetails")}</p> */}
        <div className="button-container">
          <GreenButton
            text={t("basicInfo")}
            size="medium"
            variant={toggle ? "fill" : "outlined"}
            onClick={() => setToggle(true)}
          />
          <GreenButton
            text={t("password")}
            size="medium"
            variant={!toggle ? "fill" : "outlined"}
            onClick={() => setToggle(false)}
          />
        </div>
        {errors.general && (
          <div
            className="error-message"
            style={{
              color: "#e51937",
              marginBottom: "10px",
              textAlign: "center",
            }}
          >
            {errors.general}
          </div>
        )}
        <div className="info-container">
          {toggle ? (
            <form onSubmit={handleProfileUpdate}>
              <FormControl sx={{ m: 1, flex: 1, width: "440px" }}>
                <label className="form-label">{t("fullName")}</label>
                <TextField
                  fullWidth
                  className="form-input"
                  required
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  error={!!errors.name}
                />
                {renderErrorMessage("name")}
              </FormControl>
              <FormControl sx={{ m: 1, flex: 1, width: "440px" }}>
                <label className="form-label">{t("phoneNumber")}</label>
                <TextField
                  fullWidth
                  className="form-input"
                  required
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  error={!!errors.phoneNumber}
                />
                {renderErrorMessage("phoneNumber")}
                {phoneHasChanged && (
                  <div
                    style={{
                      color: "#5cb85c",
                      fontSize: "12px",
                      marginTop: "5px",
                    }}
                  >
                    {t("otpSentNotification")}
                  </div>
                )}
              </FormControl>

              <div className="delete-btn" onClick={() => setOpenDelete(true)}>
                {t("deleteMe")} 😥
              </div>
              <GreenButton
                text={isLoading ? t("loading") : t("save")}
                size="large"
                variant="fill"
                type="submit"
                disabled={isLoading}
              />
            </form>
          ) : (
            <form onSubmit={handlePasswordUpdate}>
              {renderPasswordField(
                t("currentPassword"),
                "oldPassword",
                showOldPassword,
                handleClickShowOldPassword
              )}
              {renderPasswordField(
                t("newPassword"),
                "password",
                showPassword,
                handleClickShowPassword
              )}
              {renderPasswordField(
                t("confirmPassword"),
                "confirmPassword",
                showConfirmPassword,
                handleClickShowConfirmPassword
              )}
              <GreenButton
                text={isLoading ? t("loading") : t("save")}
                size="large"
                variant="fill"
                type="submit"
                disabled={isLoading}
              />
            </form>
          )}
        </div>
      </div>
      {renderOtpModal()}

      <MessageModals
        open={openDelete}
        currentView="delete"
        route="/users/deleteMe"
        onClose={() => {
          setOpenDelete(false);
        }}
      />
    </div>
  );
};

export default Profile;
